import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doctorsApi, type DoctorAvailabilitySlot, type DoctorAvailabilityException } from '@/api';

const DOCTOR_AVAILABILITY_KEY = (doctorId: string) => ['doctor', doctorId, 'availability'] as const;

async function fetchDoctorAvailability(doctorId: string): Promise<{
  slots: DoctorAvailabilitySlot[];
  exceptions: DoctorAvailabilityException[];
}> {
  return doctorsApi.getMyAvailability(doctorId);
}

export function useDoctorAvailability(doctorId: string) {
  return useQuery({
    queryKey: DOCTOR_AVAILABILITY_KEY(doctorId),
    queryFn: () => fetchDoctorAvailability(doctorId),
    enabled: !!doctorId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 2,
  });
}

export function useUpdateDoctorAvailability(doctorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { slots: Omit<DoctorAvailabilitySlot, 'id'>[] }) =>
      doctorsApi.updateAvailability(doctorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCTOR_AVAILABILITY_KEY(doctorId) });
    },
  });
}

export function useCreateDoctorAvailabilityException(doctorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<DoctorAvailabilityException, 'id'>) =>
      doctorsApi.createAvailabilityException(doctorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCTOR_AVAILABILITY_KEY(doctorId) });
    },
  });
}

export function useDeleteDoctorAvailabilityException(doctorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (exceptionId: string) =>
      doctorsApi.deleteAvailabilityException(doctorId, exceptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCTOR_AVAILABILITY_KEY(doctorId) });
    },
  });
}
